import { isProblemDetails, type components } from '@hpd/contract';
import {
  chargeableUnits,
  isCurrencyCode,
  Money,
  Rational,
  type RateStructureDefinition,
} from '@hpd/domain';
import { queryOptions } from '@tanstack/react-query';

import { api } from '../api.ts';
import { ApiProblem } from './me.ts';

// CB-REQ PRC-01, PRC-02, PRC-03 (v1.942). Every read here is above the price line; the screens only
// ask when /v1/me says the person can see prices, and the API refuses regardless.
//
// The price check on a product page is computed here, in the browser, with the domain package's
// own chargeableUnits() — the same function the API will price a quote line with — so the figure a
// person checks against their old system is the figure AVenue will charge.

export type PriceGroup = components['schemas']['PriceGroupView'];
export type RateStructure = components['schemas']['RateStructureView'];
export type ProductRate = components['schemas']['ProductRateView'];

function problem(error: unknown, fallback: string): ApiProblem {
  return isProblemDetails(error)
    ? new ApiProblem(error.type, error.detail ?? error.title)
    : new ApiProblem('unknown', fallback);
}

export const priceGroupsQuery = queryOptions({
  queryKey: ['price-groups'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/price-groups');
    if (error) throw problem(error, 'Could not load the price groups.');
    return data;
  },
});

export const rateStructuresQuery = queryOptions({
  queryKey: ['rate-structures'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/rate-structures');
    if (error) throw problem(error, 'Could not load the rate structures.');
    return data;
  },
});

export const productRatesQuery = (productId: string) =>
  queryOptions({
    queryKey: ['products', productId, 'rates'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/products/{id}/rates', {
        params: { path: { id: productId } },
      });
      if (error) throw problem(error, 'Could not load the rates.');
      return data;
    },
  });

export async function setProductRate(
  productId: string,
  groupId: string,
  input: { amountMinor: string; rateStructureId: string },
) {
  const { data, error } = await api.PUT('/v1/products/{id}/rates/{groupId}', {
    params: { path: { id: productId, groupId } },
    body: {
      amount: { amountMinor: input.amountMinor, currency: 'AUD' },
      rateStructureId: input.rateStructureId,
    },
  });
  if (error) throw problem(error, 'Could not save that rate.');
  return data;
}

export async function clearProductRate(productId: string, groupId: string) {
  const { data, error } = await api.DELETE('/v1/products/{id}/rates/{groupId}', {
    params: { path: { id: productId, groupId } },
  });
  if (error) throw problem(error, 'Could not remove that rate.');
  return data;
}

const rational = (r: { numerator: number; denominator: number }) =>
  Rational.of(BigInt(r.numerator), BigInt(r.denominator));

/** The API's view of a structure, as the definition the domain evaluates. */
export function toDefinition(s: RateStructure): RateStructureDefinition {
  const e = s.extrapolation;
  return {
    kind: s.kind,
    unit: s.unit ?? null,
    increment: rational(s.increment),
    rounding: s.rounding,
    breakpoints: s.breakpoints.map((b) => ({ upTo: b.upTo, charged: rational(b.charged) })),
    extrapolation: !e
      ? null
      : e.kind === 'repeat'
        ? { kind: 'repeat' }
        : e.kind === 'linear'
          ? { kind: 'linear', perUnit: rational(e.perUnit!) }
          : { kind: 'per_started_block', blockUnits: e.blockUnits!, charged: rational(e.charged!) },
    minimum: rational(s.minimum),
  };
}

/**
 * What a hire of `quantity` costs at a rate: chargeable units from the structure, times the rate,
 * rounded once (PRC-11, CB-CONV §3.4). Ex GST, before any discount — a quote adds those in order.
 */
export function priceFor(
  rate: { amountMinor: string; currency: string },
  structure: RateStructure,
  quantity: number,
): { units: Rational; total: Money } {
  if (!isCurrencyCode(rate.currency)) throw new Error(`Unknown currency ${rate.currency}`);
  const units = chargeableUnits(toDefinition(structure), Rational.fromInt(quantity));
  const total = Money.of(BigInt(rate.amountMinor), rate.currency).multiply(units).round();
  return { units, total };
}

/** "3", "4½" — a unit count as a person reads it. */
export function formatUnits(units: Rational): string {
  const { floor, remainder } = units.floorDivMod();
  if (remainder.isZero()) return floor.toString();
  if (remainder.equals(Rational.of(1n, 2n))) return `${floor === 0n ? '' : floor}½`;
  return units.toString();
}

/** A structure described in one line, for a person choosing between them. */
export function describeStructure(s: RateStructure): string {
  if (s.kind === 'fixed') return 'One charge, however long';
  const unit = s.unit ?? 'unit';
  const table = s.breakpoints.map((b) => `up to ${b.upTo} → ${formatUnits(rational(b.charged))}`);
  const e = s.extrapolation;
  const beyond = !e
    ? ''
    : e.kind === 'repeat'
      ? `then repeats every ${s.breakpoints.at(-1)?.upTo ?? '?'} ${unit}s`
      : e.kind === 'linear'
        ? `${formatUnits(rational(e.perUnit!))} per ${unit}${table.length ? ' after that' : ''}`
        : `+${formatUnits(rational(e.charged!))} per ${e.blockUnits} ${unit}s begun`;
  const increment = rational(s.increment);
  const measured =
    s.unit === 'hour' && increment.equals(Rational.of(1n, 2n)) ? 'half hours, rounded up' : null;
  const min = rational(s.minimum);
  return [
    table.length ? `${table.join(', ')} ${unit}s` : null,
    beyond,
    measured,
    min.compareTo(Rational.ONE) > 0 ? `minimum ${formatUnits(min)} ${unit}s` : null,
  ]
    .filter(Boolean)
    .join('; ');
}
